<template>
  <div :class="parentContainerClasses">
    <component
      :is="tooltipMessage ? 'side-nav-tooltip' : 'div'"
      :content="tooltipMessage"
      :is-active="active"
    >
      <router-link
        class="nav-button ng-parent-header"
        exact-path
        :to="to"
        @click.native.capture="onHeaderClick"
      >
        <slot name="parent-item">
          <span
            v-if="markColor"
            class="ng-section-mark"
            :style="{ color: markColor }"
            aria-hidden="true"
          >
            <!-- Exact Polly brand slash (same mark as the logo's teal slash),
                 colored per section — matches the DS `polly-slash` kit icon. -->
            <svg
              viewBox="77 0 21 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M93.1594 1.0918L95.4365 2.4276L81.3915 22.91H78.1855L93.1594 1.0918Z"
                fill="currentColor"
              />
            </svg>
          </span>
          <div v-if="icon" class="nav-icon">
            <slot name="icon">
              <px-icon :icon="icon"></px-icon>
            </slot>
          </div>
          <div v-if="largeText" :class="largeHeaderClasses">
            {{ shownLargeText }}
          </div>
          <div v-if="text" :class="headerClasses">
            {{ shownText }}
          </div>
        </slot>
      </router-link>
    </component>
    <div class="sub-items">
      <slot></slot>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { CLASSIC_UI_NEXTGEN_NAV } from '@shared/constants/flags';
import { stripTrailingSlash } from '@shared/utils/urlFormatters.js';
import SideNavTooltip from './SideNavTooltip.vue';

export default {
  components: { SideNavTooltip },
  provide: function () {
    return {
      setParentActive: this.setParentActive,
    };
  },
  props: {
    to: {
      type: [String, Object],
      required: true,
    },
    largeText: {
      type: String,
      default: '',
    },
    text: {
      type: String,
      default: '',
    },
    icon: {
      type: String,
      default: '',
    },
    emphasizeText: {
      type: Boolean,
      default: true,
    },
    showTooltip: {
      type: Boolean,
      default: false,
    },
    tooltip: {
      type: String,
      default: '',
    },
    // NextGen restyle: brand-colored "/" mark shown before the section label.
    markColor: {
      type: String,
      default: '',
    },
  },
  data: function () {
    return {
      active: false,
      // NextGen accordion: user-driven collapse of the active section's
      // sub-menu (independent of route-based `active`).
      collapsed: false,
    };
  },
  computed: {
    ...mapGetters({ isFlagActive: 'core/isFlagActive' }),
    isNextGenNav() {
      return this.isFlagActive(CLASSIC_UI_NEXTGEN_NAV);
    },
    // NextGen restyle: section labels are title-case (e.g. "Pricing Engine")
    // to match the DS. Only applies to section groups (emphasized), never the
    // profile group (a user name).
    shownLargeText() {
      return this.isNextGenNav
        ? this.toTitleCase(this.largeText)
        : this.largeText;
    },
    shownText() {
      // When a large label exists, `text` is the abbreviated (tablet) form —
      // keep it as authored (e.g. "PE"). Otherwise it's the section's main
      // label, which the DS renders title-cased (e.g. "Sell").
      if (this.isNextGenNav && this.emphasizeText && !this.largeText) {
        return this.toTitleCase(this.text);
      }
      return this.text;
    },
    headerClasses() {
      return {
        'nav-text': true,
        'medium-screen-text': this.largeText,
        'nav-text-header': this.emphasizeText,
      };
    },
    largeHeaderClasses() {
      const classList = ['nav-text', 'big-screen-text'];
      if (this.emphasizeText) {
        classList.push('nav-text-header');
      }
      return classList;
    },
    hasSubItems() {
      return !!(this.$slots.default && this.$slots.default.length);
    },
    parentContainerClasses() {
      const classList = ['parent-item', 'flex'];
      // NextGen: a section is expanded only when active AND not user-collapsed.
      const expanded =
        this.isNextGenNav && this.hasSubItems
          ? this.active && !this.collapsed
          : this.active;
      if (expanded) {
        classList.push('active-parent-item');
      }
      return classList;
    },
    tooltipMessage() {
      if (!this.showTooltip) {
        return null;
      }
      if (this.tooltip) {
        return this.tooltip;
      }
      if (this.largeText) {
        return this.largeText;
      }
      return this.text;
    },
    targetPath() {
      if (typeof this.to === 'object') {
        return this.$router.resolve(this.to).route.path;
      } else {
        return this.to;
      }
    },
  },
  watch: {
    $route: {
      immediate: true,
      handler(to) {
        if (
          stripTrailingSlash(to.path) === stripTrailingSlash(this.targetPath)
        ) {
          this.setParentActive();
        } else if (this.active) {
          this.active = false;
        }
      },
    },
  },
  methods: {
    setParentActive() {
      this.active = true;
      // Navigating into a section always expands it (clears manual collapse).
      this.collapsed = false;
    },
    onHeaderClick(e) {
      // NextGen accordion: clicking an expanded section header collapses its
      // sub-menu (click again to re-expand) without navigating away. Inactive
      // sections keep their normal navigate-to-landing behavior.
      if (!this.isNextGenNav || !this.hasSubItems || !this.active) {
        return;
      }
      e.preventDefault();
      this.collapsed = !this.collapsed;
    },
    toTitleCase(str) {
      return (str || '')
        .toLowerCase()
        .replace(/(^|\s)\S/g, c => c.toUpperCase());
    },
  },
};
</script>
